import { Link } from "react-router-dom";
import { ArrowRight, Info } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import Faq from "@/components/Faq";
import { IMAGES } from "@/lib/site";

const FAQS = [
  {
    q: "Are Sixth Shock and Prosperlink Riverstone services?",
    a: "No. They are independent companies. Riverstone’s role is to introduce interested businesses and help ensure the conversation is worthwhile—delivery and all offerings belong entirely to the companies themselves.",
  },
  {
    q: "Is this investment or financial advice?",
    a: "No. Nothing on this page is investment, legal, tax, or financial advice, and nothing here is an offer to buy or sell anything. Evaluate any offering directly with the provider and your own advisors.",
  },
  {
    q: "How do we learn more?",
    a: "Request information through the form on this page. We’ll arrange a conversation to understand your goals before any introduction is made.",
  },
];

export default function Emerging() {
  return (
    <>
      <Seo
        title="Emerging Opportunities — Sixth Shock & Prosperlink | Riverstone Partners"
        description="Explore introductions to Sixth Shock and Prosperlink—emerging opportunities introduced selectively by Riverstone Partners. Informational only; request a conversation to learn more."
        path="/solutions/emerging-opportunities"
      />

      <section className="border-b border-charcoal/10 bg-sage/50 py-16 md:py-24">
        <div className="shell max-w-3xl">
          <p className="eyebrow text-forest">Solutions · Emerging Opportunities</p>
          <h1 className="mt-5 font-serif text-4xl font-light leading-[1.1] tracking-tight text-navy sm:text-5xl" data-testid="emerging-title">
            Explore what’s next—with a clearer understanding.
          </h1>
          <p className="mt-6 text-lg leading-relaxed text-charcoal/80">
            Emerging Opportunities is the most selective part of our practice. We
            introduce businesses and individuals to two independent companies—Sixth Shock
            and Prosperlink—only when their offerings may genuinely fit. The pages below
            are introductions, not endorsements, and every detail is confirmed directly
            with the providers.
          </p>
        </div>
      </section>

      {/* Sixth Shock */}
      <section className="py-16 md:py-24" data-testid="sixth-shock-section">
        <div className="shell grid items-center gap-12 lg:grid-cols-2 lg:gap-20">
          <Reveal>
            <img
              src={IMAGES.mountainClouds}
              alt="Mountain peak rising above the clouds"
              width="1600"
              height="1100"
              loading="lazy"
              className="aspect-[4/3] w-full object-cover shadow-soft"
            />
          </Reveal>
          <Reveal delay={0.1}>
            <p className="eyebrow text-river">Sixth Shock</p>
            <h2 className="mt-4 font-serif text-3xl font-light leading-tight tracking-tight text-navy sm:text-4xl">
              Corporate structuring and digital asset strategy.
            </h2>
            <p className="mt-5 leading-relaxed text-charcoal/80">
              Sixth Shock is an enterprise consulting firm specializing in corporate
              structuring and digital asset strategy for companies preparing to
              participate in real-world-asset tokenization. Their published work spans
              capital strategy, regulatory structure, digital securities, investor
              infrastructure, and post-raise support.
            </p>
            <p className="mt-4 leading-relaxed text-charcoal/80">
              They serve established operators in industries such as fabrication,
              aggregates, energy, agriculture, construction, manufacturing, and
              technology—companies looking to unlock capital for future growth.
            </p>
            <Link
              to="/contact?service=emerging-opportunities"
              className="btn-primary mt-8"
              data-testid="sixth-shock-request-info"
            >
              Request Information — Sixth Shock <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Prosperlink */}
      <section className="bg-mist py-16 md:py-24" data-testid="prosperlink-section">
        <div className="shell grid items-center gap-12 lg:grid-cols-2 lg:gap-20">
          <Reveal className="lg:order-2">
            <img
              src={IMAGES.riverForest}
              alt="A river winding through a green forest"
              width="1600"
              height="1100"
              loading="lazy"
              className="aspect-[4/3] w-full object-cover shadow-soft"
            />
          </Reveal>
          <Reveal delay={0.1} className="lg:order-1">
            <p className="eyebrow text-river">Prosperlink</p>
            <h2 className="mt-4 font-serif text-3xl font-light leading-tight tracking-tight text-navy sm:text-4xl">
              Infrastructure for Trusted Smart Chain participation.
            </h2>
            <p className="mt-5 leading-relaxed text-charcoal/80">
              Prosperlink provides premium infrastructure for Trusted Smart Chain (TSC)
              node validation—node provisioning, 24/7 monitoring, and institutional-grade
              dashboards—alongside mobile wallet solutions.
            </p>
            <p className="mt-4 leading-relaxed text-charcoal/80">
              Their focus is making node operation accessible, secure, and sustainable
              for both single-node validators and enterprise-grade clusters.
            </p>
            <Link
              to="/contact?service=emerging-opportunities"
              className="btn-primary mt-8"
              data-testid="prosperlink-request-info"
            >
              Request Information — Prosperlink <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="shell max-w-4xl">
          <Reveal>
            <div className="flex items-start gap-4 border border-charcoal/15 bg-sage/40 p-7 md:p-8">
              <Info className="mt-1 h-5 w-5 shrink-0 text-forest" />
              <p className="text-sm leading-relaxed text-charcoal/75">
                Sixth Shock and Prosperlink are independent companies, not Riverstone
                services. Descriptions on this page summarize publicly available materials
                from each company and should be confirmed with the providers directly.
                This page is informational only—nothing here is an offer, investment
                advice, or a guarantee of returns, and Riverstone Partners does not
                provide investment services.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="mt-14">
            <h2 className="font-serif text-3xl font-light tracking-tight text-navy">Common questions</h2>
            <div className="mt-6">
              <Faq items={FAQS} />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
